import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import classes from '@nerdwallet/base-styles/classes';
import { spacing } from '@nerdwallet/base-styles/variables';
import StructuredContent from '@nerdwallet/structured-content';
import {
  AuthorByline,
  CenteredContainer,
  Stack,
  Box,
  Columns,
  Column,
  Text,
} from '@nerdwallet/currency';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';
import {
  useABTest,
  Experiment,
  ExperimentControl,
  ExperimentVariant,
} from '@nerdwallet/features';

import ABConfig from '~/lib/ab-config';
import ContentPageHeader from '~/client/components/content-page-header/content-page-header';
import EditorialStandards from '~/client/components/editorial-standards';
import ArticleStructuredContent from '~/client/components/article-structured-content/article-structured-content';
import {
  augmentWithToc,
  constructTableOfContentsDocument,
  constructSharingButtons,
  TocSchema,
} from '~/lib/structured-content';

import AuthorBox from '~/client/components/author-box/author-box';
import CitationList from '~/client/components/citation-list/citation-list';
import Methodology from '~/client/components/methodology/methodology';
import ConnectedOutdatedPageNotification from '~/client/components/outdated-page-notification/outdated-page-notification';
import HouseAd from '~/client/components/house-ad/house-ad';
import NextSteps from '~/client/components/next-steps/next-steps';
import RelatedLinks from '~/client/components/related-links/related-links';
import CCHeadToHead from '~/client/components/cc-head-to-head/cc-head-to-head';
import CCHeadToHeadLinks from '../cc-head-to-head-links/cc-head-to-head-links';

import styles from './general-article.module.less';
import FadeContainer from '../fade-container';
import { ArticleTemplateProps } from '~/lib/types';
import { computeDisplayedDate } from '~/lib/article-utils';
import { getFormattedLendingDate } from '~/lib/lending-article-utils';
import { LendingFeaturedImage } from '../lending-featured-image/lending-featured-image';

const GeneralArticle: React.FC<ArticleTemplateProps> = ({
  hasTableOfContents,
  tableOfContentsHeading,
  graphqlArticle,
  activeExperiments,
}) => {
  const { items: tocItems, augmentedDoc } = augmentWithToc(
    graphqlArticle.content
  );

  // Renders AB component for any active experiments
  const renderABContainers = () => {
    return activeExperiments.map((activeExperiment) => {
      const testConfig = _.find(ABConfig, {
        id: activeExperiment.experiment,
      });
      if (!testConfig) return null;
      return (
        <ABContainer experimentID={testConfig.id}>
          <Control />
          <Variant variantName={testConfig.variant} />
        </ABContainer>
      );
    });
  };

  const { ref: lazyHydrateRef } = useABTest({
    testName: 'cseo_sc_lazy_hydration',
  });

  const isInLazyExp = false;

  const { variantName } = useABTest({
    testName: 'content_nudges_share_buttons_bottom',
  });

  const showShareButtonsBottom = variantName === 'buttons_bottom';

  // TODO
  // uncomment the line below to enable back lazy hydration based on ab-experiment
  // variantName comes from the useABTest hook, add it back there when uncommenting
  // const isInLazyExp = variantName === 'is_lazy_hydrated';

  const NerdyTakeawayHighlightBox = (
    <div className={styles.takeawayBoxMargin}>
      <StructuredContent id="takeaways">
        {graphqlArticle.takeaways}
      </StructuredContent>
    </div>
  );

  const { creditCardsHeadToHead } = graphqlArticle;
  const showCCHeadToHeadExperiment = (creditCardsHeadToHead || []).length > 0;

  // Setting this up in a way to easily add other Lending specific verticals
  const isLendingVertical = ['Personal Loans'].includes(
    graphqlArticle?.primaryCategory?.name
  );

  const graphqlArticleCopy = { ...graphqlArticle };
  let lendingFormattedDisplayDate;
  let lendingFeaturedImageData;

  if (isLendingVertical) {
    lendingFormattedDisplayDate = getFormattedLendingDate(graphqlArticleCopy);

    if (graphqlArticleCopy.featuredImage) {
      lendingFeaturedImageData = graphqlArticleCopy.featuredImage;
      // Need to set to undefined so image isn't rendered in currency component
      graphqlArticleCopy.featuredImage = undefined;
    }
  }

  return (
    <Box paddingTop="3">
      <CenteredContainer>
        {renderABContainers()}
        {!graphqlArticle.heroBannerDocument && (
          <React.Fragment>
            <ContentPageHeader
              article={graphqlArticleCopy}
              category={null}
              width={{ desktop: 8, mobile: 12 }}
              imageHasFullWidth
              hideEditorialStandards
              isLendingVertical={isLendingVertical}
            />
            {lendingFormattedDisplayDate && (
              <Box className={styles['last-updated-date']}>
                <Text size="small" light>
                  {`Last updated on ${lendingFormattedDisplayDate}`}
                </Text>
              </Box>
            )}
            {lendingFeaturedImageData && (
              <Columns>
                <Column width={{ desktop: 8, mobile: 12 }}>
                  <Box marginBottom={3}>
                    <LendingFeaturedImage data={lendingFeaturedImageData} />
                  </Box>
                </Column>
              </Columns>
            )}
            <StructuredContent>
              {constructSharingButtons(['twitter', 'facebook', 'email'])}
            </StructuredContent>
          </React.Fragment>
        )}
        <Box marginTop="2" />
        <Stack spacing={4}>
          <Columns>
            <Column width={{ desktop: 8, mobile: 12 }}>
              {graphqlArticle.heroBannerDocument &&
                graphqlArticle.showAuthorByline && (
                  <Box marginBottom="4">
                    <AuthorByline
                      authoringDate={
                        !isLendingVertical &&
                        computeDisplayedDate({
                          displayDate: graphqlArticle.dateTimeDisplayDate,
                          showLastModifiedDate:
                            graphqlArticle.showLastModifiedDate,
                          hideDate: graphqlArticle.hideDate,
                          marketplaceEntities: null,
                        })
                      }
                      authors={graphqlArticle.authors.map((author) => ({
                        name: author.name,
                        url: author.link,
                      }))}
                    />
                    {lendingFormattedDisplayDate && (
                      <Box marginTop={2}>
                        <Text size="small" light>
                          {`Last updated on ${lendingFormattedDisplayDate}`}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              {graphqlArticle.showEditorialDisclaimer && (
                <Box marginBottom="4">
                  <EditorialStandards article={graphqlArticle} />
                </Box>
              )}
              <ConnectedOutdatedPageNotification />
              {showCCHeadToHeadExperiment && (
                <Experiment testName="ccs_h2h_apply_now_module_experiment">
                  <ExperimentControl />
                  <ExperimentVariant name="treatment">
                    <CCHeadToHead article={graphqlArticle} />
                  </ExperimentVariant>
                </Experiment>
              )}
              <Stack spacing={4}>
                {hasTableOfContents && !!tocItems.length && (
                  <div className={classNames(styles['toc-wide-variant'])}>
                    <div className={styles.hideNotDesktop}>
                      <StructuredContent>
                        {constructTableOfContentsDocument({
                          items: tocItems,
                          hasJumpCTA: true,
                          showMore: true,
                          title: tableOfContentsHeading,
                        })}
                      </StructuredContent>
                      {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                    </div>
                    <div className={styles.hideDesktop}>
                      <StructuredContent>
                        {constructTableOfContentsDocument({
                          items: tocItems,
                          hasJumpCTA: true,
                          title: tableOfContentsHeading,
                        })}
                      </StructuredContent>
                      {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                    </div>
                  </div>
                )}
                <main>
                  <div
                    ref={lazyHydrateRef}
                    className={
                      showCCHeadToHeadExperiment
                        ? classes(['margin-bottom-2', 'margin-bottom-3--wide'])
                        : classes('margin-bottom-4')
                    }
                  >
                    {hasTableOfContents ? (
                      <ArticleStructuredContent
                        lazy={isInLazyExp}
                        schema={TocSchema}
                      >
                        {augmentedDoc}
                      </ArticleStructuredContent>
                    ) : (
                      <React.Fragment>
                        {graphqlArticle.takeaways && NerdyTakeawayHighlightBox}
                        <ArticleStructuredContent lazy={isInLazyExp}>
                          {graphqlArticle.content}
                        </ArticleStructuredContent>
                      </React.Fragment>
                    )}
                  </div>
                  <CitationList />
                  {showCCHeadToHeadExperiment && (
                    <Experiment testName="ccs_h2h_apply_now_footer_links_experiment">
                      <ExperimentControl />
                      <ExperimentVariant name="treatment">
                        <CCHeadToHeadLinks article={graphqlArticle} />
                      </ExperimentVariant>
                    </Experiment>
                  )}
                  <AuthorBox article={graphqlArticle} />
                  {showShareButtonsBottom && (
                    <StructuredContent>
                      {constructSharingButtons([
                        'twitter',
                        'facebook',
                        'email',
                      ])}
                    </StructuredContent>
                  )}
                  <Methodology article={graphqlArticle} />
                </main>
              </Stack>
            </Column>
            {(RelatedLinks.shouldShow(graphqlArticle) ||
              graphqlArticle.sidebarDocument) && (
              <Column width={{ desktop: 4 }}>
                <Box
                  display={{ desktop: 'block', mobile: 'none' }}
                  position="relative"
                  height="100"
                  paddingLeft="3-half"
                  style={{ paddingTop: '14.5em' }}
                >
                  <FadeContainer offset={spacing['2']} fadeDistance={250}>
                    {graphqlArticle.sidebarDocument && (
                      <StructuredContent id="sidebarDocument">
                        {graphqlArticle.sidebarDocument}
                      </StructuredContent>
                    )}
                    {RelatedLinks.shouldShow(graphqlArticle) && (
                      <RelatedLinks article={graphqlArticle} maxItems={4} />
                    )}
                  </FadeContainer>
                </Box>
              </Column>
            )}
          </Columns>
          {graphqlArticle.bottomDocument && (
            <StructuredContent id="bottomDocument">
              {graphqlArticle.bottomDocument}
            </StructuredContent>
          )}
          {NextSteps.shouldShow(graphqlArticle) && (
            <NextSteps article={graphqlArticle} />
          )}
          <section className={styles.bottom}>
            {HouseAd.shouldShow(graphqlArticle) && (
              <Box marginBottom={4}>
                <HouseAd id={graphqlArticle.houseAd?.id} />
              </Box>
            )}
          </section>
        </Stack>
      </CenteredContainer>
      {graphqlArticle.bottomStickyDocument && (
        <div className={styles.stickyBottomBanner}>
          <StructuredContent>
            {graphqlArticle.bottomStickyDocument}
          </StructuredContent>
        </div>
      )}
    </Box>
  );
};

GeneralArticle.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  activeExperiments: [],
};

export default GeneralArticle;
